import React from 'react';
import { Select, SelectItem } from '@heroui/react';
import { useTranslation } from 'react-i18next';

interface RoleSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  isInvalid?: boolean;
  errorMessage?: string;
  isRequired?: boolean;
  label?: string;
  variant?: 'flat' | 'bordered' | 'faded' | 'underlined';
  className?: string;
  'aria-label'?: string;
  showEmptyOption?: boolean;
  isDisabled?: boolean;
}

const RoleSelector: React.FC<RoleSelectorProps> = ({
  value,
  onChange,
  placeholder,
  isInvalid = false,
  errorMessage,
  isRequired = false,
  label,
  variant,
  className,
  'aria-label': ariaLabel,
  showEmptyOption = false,
  isDisabled = false,
}) => {
  const { t } = useTranslation('admin');

  const selectLabel = label || t('users.form.role.label', { defaultValue: 'Role' });
  const selectPlaceholder = placeholder || t('users.form.role.placeholder', { defaultValue: 'Select role' });
  const selectAriaLabel = ariaLabel || (label === '' ? selectPlaceholder : undefined);

  return (
    <Select
      label={selectLabel}
      placeholder={selectPlaceholder}
      selectedKeys={value !== undefined && value !== null ? [value] : []}
      onSelectionChange={(keys) => {
        const selectedValue = Array.from(keys)[0] as string;
        onChange(selectedValue ?? '');
      }}
      isRequired={isRequired}
      variant={variant}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      isDisabled={isDisabled}
      className={className}
      selectionMode="single"
      aria-label={selectAriaLabel || undefined}
    >
      {showEmptyOption ? (
        <SelectItem key="">{t('users.filters.allRoles', { defaultValue: 'All Roles' })}</SelectItem>
      ) : null}
      <SelectItem key="learner">{t('users.roles.learner', { defaultValue: 'Learner' })}</SelectItem>
      <SelectItem key="creator">{t('users.roles.creator', { defaultValue: 'Creator' })}</SelectItem>
      <SelectItem key="admin">{t('users.roles.admin', { defaultValue: 'Admin' })}</SelectItem>
    </Select>
  );
};

export default RoleSelector;
